import React, { useState } from 'react';
import {
  Box, FormControl, InputAdornment, InputLabel, MenuItem, OutlinedInput,
  Select, Typography, IconButton
} from '@mui/material';
import { IoMdClose, IoMdSearch } from 'react-icons/io';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faSearch } from '@fortawesome/free-solid-svg-icons';
import { ESTABLISHMENT_TYPE } from '../../utils/constants';
import {
  EMPLOYEE_UPLOAD_MONTH_LIST,
  generateEmployeeUploadYearsList,
  EmployeeUploadFilterState,
  EmployeeUploadSearchConfig,
  sortEmployeeUploadCompaniesByName
} from '../../utils/employeeUploads.utils';
import { toast } from 'react-toastify';

interface EmployeeUploadFiltersProps {
  filters: EmployeeUploadFilterState;
  searchConfig: EmployeeUploadSearchConfig;
  companies: any[];
  associateCompanies: any[];
  locations: any[];

  onUpdateFilter: (field: keyof EmployeeUploadFilterState, value: any) => void;
  onUpdateSearchConfig: (updates: Partial<EmployeeUploadSearchConfig>) => void;
  onSearch: () => void;
  onClearSearch: () => void;

  // Configuration
  showYearMonth?: boolean;
  showAdditionalFilters?: boolean;
  onAdditionalFiltersClick?: () => void;
  searchPlaceholder?: string;
  searchLabel?: string;
}

const EmployeeUploadFilters: React.FC<EmployeeUploadFiltersProps> = ({
  filters,
  searchConfig,
  companies,
  associateCompanies,
  locations,
  onUpdateFilter,
  onUpdateSearchConfig,
  onSearch,
  onClearSearch,
  showYearMonth = false,
  showAdditionalFilters = false,
  onAdditionalFiltersClick,
  searchPlaceholder = "Search",
  searchLabel = "Search (Employee Code)"
}) => {
  const establishmentTypes = Object.values(ESTABLISHMENT_TYPE);
  const yearsList = generateEmployeeUploadYearsList();

  const handleEstablishmentTypeChange = (event: any) => {
    onUpdateFilter('establishmentType', event.target.value);
  };

  const handleCompanyChange = (event: any) => {
    onUpdateFilter('company', event.target.value);
  };

  const handleAssociateCompanyChange = (event: any) => {
    onUpdateFilter('associateCompany', event.target.value);
  };

  const handleLocationChange = (event: any) => {
    onUpdateFilter('location', event.target.value);
  };

  const handleYearChange = (event: any) => {
    onUpdateFilter('year', event.target.value);
  };

  const handleMonthChange = (event: any) => {
    onUpdateFilter('month', event.target.value);
  };

  const handleSearchInputChange = (event: any) => {
    onUpdateSearchConfig({ searchInput: event.target.value });
  };


  return (
    <div style={{ display: 'flex' }}>
      {/* Establishment Type */}
      <Box sx={{ mr: 1 }}>
        <Typography mb={1}>Establishment Type</Typography>
        <FormControl sx={{ width: '100%', maxWidth: '200px', backgroundColor: '#ffffff', borderRadius: '5px' }} size="small">
          <Select
            sx={{ '.MuiOutlinedInput-notchedOutline': { border: 0 } }}
            value={filters.establishmentType}
            displayEmpty
            onChange={handleEstablishmentTypeChange}
            MenuProps={{
              PaperProps: {
                sx: {
                  maxHeight: 200,
                  width: 230,
                  marginTop: "3px"
                }
              }
            }}
          >
            <MenuItem key="all_option" sx={{ width: '240px' }} value="">
              All
            </MenuItem>
            {establishmentTypes.map((each: any) => (
              <MenuItem key={each} sx={{ width: '240px', whiteSpace: 'initial' }} value={each}>
                {each}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
      </Box>

      {/* Company */}
      <Box sx={{ mr: 1 }}>
        <Typography mb={1}>Company</Typography>
        <FormControl sx={{ width: '100%', maxWidth: '200px', backgroundColor: '#ffffff', borderRadius: '5px' }} size="small">
          <Select
            sx={{ '.MuiOutlinedInput-notchedOutline': { border: 0 } }}
            value={filters.company}
            displayEmpty
            onChange={handleCompanyChange}
            MenuProps={{
              PaperProps: {
                sx: {
                  maxHeight: 200,
                  width: 230,
                  marginTop: "3px"
                }
              }
            }}
          >
            <MenuItem disabled sx={{ display: 'none' }} value="">
              Select Company
            </MenuItem>
            {sortEmployeeUploadCompaniesByName(companies).map((each: any) => (
              <MenuItem key={each.id} sx={{ width: '240px', whiteSpace: 'initial' }} value={each.id}>
                {each.name}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
      </Box>

      {/* Associate Company */}
      <Box sx={{ mr: 1 }}>
        <Typography mb={1}>Associate Company</Typography>
        <FormControl sx={{ width: '100%', maxWidth: '200px', backgroundColor: '#ffffff', borderRadius: '5px' }} size="small">
          <Select
            sx={{ '.MuiOutlinedInput-notchedOutline': { border: 0 } }}
            displayEmpty
            value={filters.associateCompany}
            disabled={!filters.company}
            onChange={handleAssociateCompanyChange}
            MenuProps={{
              PaperProps: {
                sx: {
                  maxHeight: 215,
                  width: 230,
                  marginTop: '3px'
                },
              },
            }}
          >
            <MenuItem disabled sx={{ display: 'none' }} value="">
              Select Associate Company
            </MenuItem>
            {sortEmployeeUploadCompaniesByName(associateCompanies).map((each: any) => (
              <MenuItem key={each.id} sx={{ width: '240px', whiteSpace: 'initial' }} value={each.id}>
                {each.name}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
      </Box>

      {/* Location */}
      <Box sx={{ mr: 1 }}>
        <Typography mb={1}>Location</Typography>
        <FormControl
          sx={{ width: '100%', maxWidth: '200px', backgroundColor: '#ffffff', borderRadius: '5px' }}
          size="small"
        >
          <Select
            sx={{ '.MuiOutlinedInput-notchedOutline': { border: 0 } }}
            displayEmpty
            value={filters.location}
            disabled={!filters.associateCompany}
            onChange={handleLocationChange}
            MenuProps={{
              PaperProps: {
                sx: {
                  maxHeight: 200,
                  width: 230,
                  marginLeft: '27px',
                  marginTop: '3px',
                },
              },
            }}
          >
            <MenuItem disabled sx={{ display: 'none' }} value="">
              Select Location
            </MenuItem>
            {locations && locations.length > 0 ? (
              locations.map((each: any) => {
                const { name, code, state, city }: any = each.location || {};

                if (!each.location_id || !state) {
                  return null;
                }

                return (
                  <MenuItem key={each.location_id + '^' + state.id} sx={{ whiteSpace: 'initial' }} value={each.location_id + '^' + state.id}>
                    {`${name} (${state.code}-${city.code}-${code})`}
                  </MenuItem>
                );
              })
            ) : (
              <MenuItem disabled value="">
                Select Location
              </MenuItem>
            )}
          </Select>
        </FormControl>
      </Box>

      {/* Year - Only show if showYearMonth is true */}
      {showYearMonth && (
        <Box sx={{ mr: 1 }}>
          <Typography mb={1}>Year</Typography>
          <FormControl sx={{ width: '100%', maxWidth: '200px', backgroundColor: '#ffffff', borderRadius: '5px' }} size="small">
            <Select
              sx={{ '.MuiOutlinedInput-notchedOutline': { border: 0 } }}
              displayEmpty
              value={filters.year}
              onChange={handleYearChange}
              MenuProps={{
                PaperProps: {
                  sx: {
                    maxHeight: 200,
                    width: 100,
                    marginTop: "3px"
                  }
                }
              }}
            >
              <MenuItem disabled sx={{ display: 'none' }} value="">
                Select Year
              </MenuItem>
              {yearsList.map((each: any) => (
                <MenuItem key={each} value={each}>{each}</MenuItem>
              ))}
            </Select>
          </FormControl>
        </Box>
      )}

      {/* Month - Only show if showYearMonth is true */}
      {showYearMonth && (
        <Box sx={{ mr: 1 }}>
          <Typography mb={1}>Month</Typography>
          <FormControl sx={{ width: '100%', maxWidth: '200px', backgroundColor: '#ffffff', borderRadius: '5px' }} size="small">
            <Select
              sx={{ '.MuiOutlinedInput-notchedOutline': { border: 0 } }}
              displayEmpty
              value={filters.month}
              disabled={!filters.year}
              onChange={handleMonthChange}
              MenuProps={{
                PaperProps: {
                  sx: {
                    maxHeight: 200,
                    width: 100,
                    marginTop: "3px"
                  }
                }
              }}
            >
              <MenuItem disabled sx={{ display: 'none' }} value="">
                Select Month
              </MenuItem>
              {EMPLOYEE_UPLOAD_MONTH_LIST.map((each: any) => (
                <MenuItem key={each} value={each}>{each}</MenuItem>
              ))}
            </Select>
          </FormControl>
        </Box>
      )}

      {/* Search Input */}
      <Box sx={{ mr: 1 }}>
        <Typography mb={1}>{searchLabel}</Typography>
        <FormControl sx={{ width: '100%', backgroundColor: '#ffffff', borderRadius: '5px' }} size="small">
          <InputLabel htmlFor="outlined-adornment-search">{searchPlaceholder}</InputLabel>
          <OutlinedInput
            value={searchConfig.searchInput}
            onChange={handleSearchInputChange}
            sx={{ '.MuiOutlinedInput-notchedOutline': { border: 0 } }}
            id="outlined-adornment-search"
            type='text'
            endAdornment={
              <InputAdornment position="end">
                {searchConfig.searchInput && (
                  <IconButton
                    onClick={onClearSearch}
                    edge="end"
                  >
                    <IoMdClose />
                  </IconButton>
                )}
                <IconButton
                  onClick={onSearch}
                  edge="end"
                >
                  <IoMdSearch />
                </IconButton>
              </InputAdornment>
            }
            label={searchPlaceholder}
          />
        </FormControl>
      </Box>

      {/* Additional Filters (conditional) */}
      {showAdditionalFilters && filters.establishmentType === 'CLRA' && (
        <div className="d-flex align-items-end me-3">
          <div className="d-flex flex-column">
            <button
              type="submit"
              className="btn btn-primary d-flex align-items-center"
              onClick={onAdditionalFiltersClick}
            >
              <FontAwesomeIcon icon={faSearch} />
              <span className="ms-2">Additional Filters</span>
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default EmployeeUploadFilters;